/**
 * DOM primitives for the CS HUD.
 *
 * Every write goes through a cached setter: the style/text node is only touched
 * when the value actually changed, and every value is quantised so the string
 * that reaches the DOM is one of a small fixed set. Combined with the
 * pre-built node trees in each widget this keeps `update()` allocation-free.
 */

export const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
export const clamp01 = (v) => (v > 0 ? (v < 1 ? v : 1) : 0);

export function el(tag, cls, parent, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  if (parent) parent.appendChild(n);
  return n;
}

/** Text, cached against the raw value. */
export function setText(node, v) {
  if (node.__t !== v) {
    node.__t = v;
    node.textContent = v;
  }
}

/** Integer text, cached. `node.textContent = number` is legal and cheap. */
export function setNum(node, v) {
  const n = Math.round(v) | 0;
  if (node.__n !== n) {
    node.__n = n;
    node.textContent = n;
  }
}

export function setStyle(node, prop, v) {
  const key = '__s_' + prop;
  if (node[key] !== v) {
    node[key] = v;
    node.style.setProperty(prop, v);
  }
}

export function setClass(node, cls, on) {
  const key = '__c_' + cls;
  if (node[key] !== on) {
    node[key] = on;
    node.classList.toggle(cls, on);
  }
}

/** Width/height as a percentage of the parent. Quantised to whole percent. */
export function setPct(node, prop, frac) {
  const p = Math.round(clamp01(frac) * 100);
  const key = '__p_' + prop;
  if (node[key] !== p) {
    node[key] = p;
    node.style.setProperty(prop, p + '%');
  }
}

/** Opacity, quantised to 1/50 so at most 51 distinct strings are ever made. */
export function setOpacity(node, v) {
  const q = Math.round(clamp01(v) * 50) / 50;
  if (node.__o !== q) {
    node.__o = q;
    node.style.setProperty('opacity', q === 0 ? '0' : String(q));
  }
}

/** Rotation in degrees, quantised to whole degrees. */
export function setRotate(node, deg) {
  const d = Math.round(deg);
  if (node.__r !== d) {
    node.__r = d;
    node.style.setProperty('transform', 'rotate(' + d + 'deg)');
  }
}

export function setHidden(node, hidden) {
  setStyle(node, 'display', hidden ? 'none' : '');
}

/** MM:SS, rewritten only when the whole-second value changes. */
export function setClock(node, seconds) {
  const total = seconds > 0 ? Math.ceil(seconds) : 0;
  if (node.__sec === total) return;
  node.__sec = total;
  const m = (total / 60) | 0;
  const s = total % 60;
  node.textContent = m + ':' + (s < 10 ? '0' + s : s);
}
